// Utility to test image URLs and diagnose loading issues
export const imageUrlTester = {
  // Test if an image URL is accessible
  async testImageUrl(url: string): Promise<{
    accessible: boolean;
    error?: string;
    status?: number;
    corsEnabled?: boolean;
    dimensions?: { width: number; height: number };
  }> {
    console.log('🔍 Testing image URL:', url);
    
    try {
      // Test 1: Direct fetch
      const response = await fetch(url, { 
        mode: 'cors',
        headers: {
          'Accept': 'image/*'
        }
      });
      
      const corsHeader = response.headers.get('access-control-allow-origin');
      const corsEnabled = corsHeader === '*' || corsHeader === window.location.origin;
      
      if (!response.ok) {
        return {
          accessible: false,
          error: `HTTP ${response.status}: ${response.statusText}`,
          status: response.status,
          corsEnabled
        };
      }
      
      // Test 2: Image loading
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          resolve({
            accessible: true,
            status: response.status,
            corsEnabled,
            dimensions: {
              width: img.naturalWidth,
              height: img.naturalHeight
            }
          });
        };
        
        img.onerror = () => {
          resolve({
            accessible: false,
            error: 'Image failed to load (CORS or network issue)',
            status: response.status,
            corsEnabled
          });
        };
        
        img.src = url;
      });
      
    } catch (error) {
      return {
        accessible: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        corsEnabled: false
      };
    }
  },

  // Get detailed diagnostic information
  async diagnoseImageUrl(url: string): Promise<string> {
    const result = await this.testImageUrl(url);
    
    let diagnosis = `Image URL Diagnosis:\n`;
    diagnosis += `URL: ${url}\n`;
    diagnosis += `Accessible: ${result.accessible ? '✅ Yes' : '❌ No'}\n`;
    
    if (result.status) {
      diagnosis += `HTTP Status: ${result.status}\n`;
    }
    
    if (result.corsEnabled !== undefined) {
      diagnosis += `CORS Enabled: ${result.corsEnabled ? '✅ Yes' : '❌ No'}\n`;
    }
    
    if (result.dimensions) {
      diagnosis += `Dimensions: ${result.dimensions.width}x${result.dimensions.height}\n`;
    }
    
    if (result.error) {
      diagnosis += `Error: ${result.error}\n`;
    }
    
         // Provide recommendations
     diagnosis += `\nRecommendations:\n`;
     if (!result.accessible) {
       if (result.status === 403) {
         diagnosis += `• This is a CORS issue - the server is blocking cross-origin requests\n`;
         diagnosis += `• Try uploading the image directly instead\n`;
       } else if (result.status === 404) {
         diagnosis += `• Image not found - URL may be expired or image removed\n`;
         diagnosis += `• Try regenerating the image or uploading a new one\n`;
       } else if (result.status && result.status >= 500) {
         diagnosis += `• Server error - try again later\n`;
       } else {
         diagnosis += `• Network or server issue - try refreshing the page\n`;
       }
     } else {
       diagnosis += `• Image is accessible and should work\n`;
     }
    
    return diagnosis;
  },

  // Test multiple URLs and provide summary
  async testMultipleUrls(urls: string[]): Promise<{
    total: number;
    accessible: number;
    failed: number;
    results: Array<{ url: string; accessible: boolean; error?: string }>;
  }> {
    const results = await Promise.all(
      urls.map(async (url) => {
        const result = await this.testImageUrl(url);
        return {
          url,
          accessible: result.accessible,
          error: result.error
        };
      })
    );
    
    const accessible = results.filter(r => r.accessible).length;
    const failed = results.filter(r => !r.accessible).length;
    
    return {
      total: urls.length,
      accessible,
      failed,
      results
    };
  }
}; 